(function (root) {
  'use strict';

  function number(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function integer(value, fallback) {
    var parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function optimiseRolls(requiredMetres, meshProducts) {
    var required = Math.max(0, number(requiredMetres, 0));
    var sizes = (meshProducts || [])
      .filter(function (item) { return item && item.available && item.variantId && number(item.price, 0) > 0 && number(item.size, 0) > 0; })
      .sort(function (a, b) { return b.size - a.size; });

    if (required === 0) return { lines: [], supplied: 0, spare: 0, rolls: 0, cost: 0 };
    if (!sizes.length) return null;

    var best = null;
    var smallest = sizes[sizes.length - 1];
    var larger = sizes.slice(0, -1);
    var quantities = {};

    function consider() {
      var covered = 0;
      var cost = 0;
      var rolls = 0;
      var lines = [];

      sizes.forEach(function (item) {
        var qty = quantities[item.size] || 0;
        if (!qty) return;
        covered += qty * item.size;
        cost += qty * item.price;
        rolls += qty;
        lines.push({ size: item.size, quantity: qty, price: item.price, variantId: item.variantId, title: item.title });
      });

      if (covered < required) return;
      var candidate = { lines: lines, supplied: covered, spare: covered - required, rolls: rolls, cost: cost };
      if (!best ||
        candidate.spare < best.spare ||
        (candidate.spare === best.spare && candidate.rolls < best.rolls) ||
        (candidate.spare === best.spare && candidate.rolls === best.rolls && candidate.cost < best.cost)) {
        best = candidate;
      }
    }

    function enumerate(index, covered) {
      if (index === larger.length) {
        quantities[smallest.size] = Math.max(0, Math.ceil((required - covered) / smallest.size));
        consider();
        return;
      }

      var item = larger[index];
      var limit = Math.ceil(required / item.size) + 1;
      for (var qty = 0; qty <= limit; qty += 1) {
        quantities[item.size] = qty;
        enumerate(index + 1, covered + qty * item.size);
      }
    }

    enumerate(0, 0);
    return best;
  }

  function calculatePlan(input, config) {
    var rawLength = Math.max(0, number(input.length, 0));
    var boundary = input.unit === 'km' ? rawLength * 1000 : rawLength;
    if (!Number.isFinite(boundary) || boundary > 100000) return { error: config.lengthLimitMessage || 'This calculator supports up to 100km per plan. Contact Outback for a larger project.' };
    var spacing = input.spacing === 'custom' ? number(input.customSpacing, 5) : number(input.spacing, 5);
    spacing = Math.min(8, Math.max(3, spacing));

    var gateways = input.layout === 'closed' ? Math.max(0, integer(input.gateways, 0)) : 0;
    var gateWidth = input.layout === 'closed' ? Math.max(0, number(input.gateWidth, 0)) : 0;
    if (gateWidth > boundary) return { error: 'Gate opening width cannot be greater than the total boundary length.' };

    var netLength = Math.max(0, boundary - gateWidth);
    var mesh = optimiseRolls(netLength, config.mesh);
    var spans = netLength > 0 ? Math.ceil(netLength / spacing) : 0;
    var customStrainerPosts = Math.max(0, integer(input.customStrainers, 0));
    var strainerPosts = input.layout === 'closed' ? 4 : input.layout === 'custom' ? customStrainerPosts : (netLength > 0 ? 2 : 0);
    var picketsRequired = 0;
    if (netLength > 0) {
      if (input.layout === 'closed') picketsRequired = Math.max(spans - 4, 0);
      else if (input.layout === 'custom') picketsRequired = Math.max(spans + 1 - customStrainerPosts, 0);
      else picketsRequired = Math.max(spans - 1, 0);
    }
    var pack = input.finish === 'bitumen' ? config.pickets.bitumen : input.finish === 'galvanised' ? config.pickets.galvanised : null;
    // A pack with no live price (e.g. product archived) is priced on request, never shown as $0.00.
    var packPriced = !!(pack && number(pack.price, 0) > 0);
    var packCount = pack && picketsRequired > 0 ? Math.ceil(picketsRequired / pack.quantity) : 0;
    var picketsSupplied = packCount * (pack ? pack.quantity : 0);
    var picketsSpare = pack ? picketsSupplied - picketsRequired : 0;
    var clipCount = picketsRequired * 16;
    var clipBoxes = clipCount > 0 ? Math.ceil(clipCount / config.clips.boxQuantity) : 0;
    var topStrands = integer(input.topProtection, 0);
    var topBarbRolls = topStrands > 0 && netLength > 0 ? Math.ceil((netLength * topStrands) / config.barbed.rollLength) : 0;
    var lowBarbRolls = input.groundProtection === 'low-barb' && netLength > 0 ? Math.ceil(netLength / config.barbed.rollLength) : 0;

    var knownSubtotal = 0;
    if (mesh) knownSubtotal += mesh.cost;
    if (pack) knownSubtotal += packCount * pack.price;
    knownSubtotal += clipBoxes * number(config.clips.price, 0);
    knownSubtotal += (topBarbRolls + lowBarbRolls) * number(config.barbed.price, 0);
    knownSubtotal += strainerPosts * number(config.strainer.postPrice, 0);

    var meshPerMetre = mesh && mesh.supplied > 0 ? mesh.cost / mesh.supplied : 0;
    var picketPerUnit = pack && pack.quantity > 0 ? pack.price / pack.quantity : 0;

    return {
      boundary: boundary,
      netLength: netLength,
      spacing: spacing,
      spans: spans,
      gateways: gateways,
      gateWidth: gateWidth,
      mesh: mesh,
      picketsRequired: picketsRequired,
      pack: pack,
      packCount: packCount,
      picketsSupplied: picketsSupplied,
      picketsSpare: picketsSpare,
      clipCount: clipCount,
      clipBoxes: clipBoxes,
      topStrands: topStrands,
      topBarbRolls: topBarbRolls,
      lowBarbRolls: lowBarbRolls,
      strainerPosts: strainerPosts,
      groundProtection: input.groundProtection,
      layout: input.layout,
      meshPerMetre: meshPerMetre,
      picketPerUnit: picketPerUnit,
      packPriced: packPriced,
      knownSubtotal: knownSubtotal
    };
  }

  function formatMoney(value) {
    return '$' + number(value, 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function formatNumber(value, digits) {
    return number(value, 0).toLocaleString('en-AU', { maximumFractionDigits: digits == null ? 2 : digits });
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>'"]/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character];
    });
  }

  function readInputs(container) {
    function value(name) {
      var element = container.querySelector('[name="' + name + '"]:checked') || container.querySelector('[name="' + name + '"]');
      return element ? element.value : '';
    }
    var ownPickets = container.querySelector('[name="ef_own_pickets"]');
    return {
      length: value('ef_length'),
      unit: value('ef_unit'),
      spacing: value('ef_spacing'),
      customSpacing: value('ef_custom_spacing'),
      finish: ownPickets && ownPickets.checked ? 'own' : value('ef_finish'),
      topProtection: value('ef_top'),
      groundProtection: value('ef_ground'),
      layout: value('ef_layout'),
      customStrainers: value('ef_custom_strainers'),
      gateways: value('ef_gateways'),
      gateWidth: value('ef_gate_width')
    };
  }

  function materialList(plan, config) {
    if (plan.error) return plan.error;
    var lines = ['OUTBACK FIXED KNOT & EXCLUSION FENCING — MATERIALS SCHEDULE'];
    lines.push('Boundary: ' + formatNumber(plan.boundary) + 'm');
    if (plan.gateWidth > 0) lines.push('Gate openings: ' + formatNumber(plan.gateWidth) + 'm (' + plan.gateways + ' gateways)');
    lines.push('Net mesh: ' + formatNumber(plan.netLength) + 'm');
    lines.push('Layout: ' + (plan.layout === 'closed' ? 'Closed paddock' : plan.layout === 'custom' ? 'Custom' : 'Straight run') + '; picket spacing: ' + formatNumber(plan.spacing) + 'm');
    if (plan.mesh) {
      lines.push('Fixed Knot rolls: ' + plan.mesh.lines.map(function (line) { return line.quantity + ' × ' + line.size + 'm'; }).join(' + '));
      lines.push('Mesh: ' + formatMoney(plan.mesh.cost) + '; ' + formatMoney(plan.meshPerMetre) + ' per metre supplied');
      lines.push('Mesh supplied: ' + formatNumber(plan.mesh.supplied) + 'm; spare: ' + formatNumber(plan.mesh.spare) + 'm');
    } else if (plan.netLength > 0) {
      lines.push('Fixed Knot rolls: live product data unavailable — quote required');
    }
    lines.push('240cm pickets required: ' + plan.picketsRequired);
    if (plan.pack && !plan.packPriced) lines.push('240cm pickets: price on request (call Daryl); not included in the subtotal');
    else if (plan.pack) lines.push(plan.pack.title + ': ' + plan.packCount + ' pack(s); ' + plan.picketsSupplied + ' supplied; ' + plan.picketsSpare + ' spare; ' + formatMoney(plan.packCount * plan.pack.price) + ' (' + formatMoney(plan.picketPerUnit) + ' per picket)');
    else lines.push('Pickets: customer supplying their own');
    lines.push('3.25m heavy strainer posts: ' + plan.strainerPosts + ' @ ' + formatMoney(config.strainer.postPrice) + ' post-only; stays and assembly hardware to confirm');
    lines.push('C24 clips: ' + plan.clipCount + '; boxes of 10,000: ' + plan.clipBoxes + '; ' + formatMoney(plan.clipBoxes * config.clips.price));
    lines.push('Top barb rolls: ' + plan.topBarbRolls + '; low-barb rolls: ' + plan.lowBarbRolls + '; ' + formatMoney((plan.topBarbRolls + plan.lowBarbRolls) * config.barbed.price));
    if (plan.groundProtection === 'hinged-apron') lines.push('Standalone hinged apron: ' + formatNumber(plan.netLength) + 'm — quote only');
    if (plan.groundProtection === 'radius-apron') lines.push('Radius apron: information only — no current cart product');
    if (plan.gateWidth > 0) lines.push('Exclusion gates and gateway assemblies: quote only');
    lines.push('Known subtotal: ' + formatMoney(plan.knownSubtotal) + ' inc GST');
    lines.push('Available materials cart subtotal: ' + formatMoney(cartSubtotal(plan, config)) + ' inc GST');
    lines.push('Not added to cart: planning strainers; unavailable products; quote-only stays, assembly hardware, aprons and gates. Known subtotal is not a complete delivered fence quote.');
    lines.push('Freight: quoted separately');
    lines.push('Long runs, gullies, watercourses, gateways and changes in direction may require additional strainer assemblies. Outback will confirm the final layout with your plan.');
    return lines.join('\n');
  }

  function cartItems(plan, config) {
    if (!plan || plan.error) return [];
    var byVariant = {};
    function add(id, quantity) {
      if (!id || quantity <= 0) return;
      byVariant[id] = (byVariant[id] || 0) + quantity;
    }
    if (plan.mesh) plan.mesh.lines.forEach(function (line) { add(line.variantId, line.quantity); });
    if (plan.pack && plan.pack.available) add(plan.pack.variantId, plan.packCount);
    if (config.clips.available) add(config.clips.variantId, plan.clipBoxes);
    if (config.barbed.available) add(config.barbed.variantId, plan.topBarbRolls + plan.lowBarbRolls);
    return Object.keys(byVariant).map(function (id) { return { id: parseInt(id, 10), quantity: byVariant[id] }; });
  }

  function cartSubtotal(plan, config) {
    if (!plan || plan.error) return 0;
    var total = plan.mesh ? plan.mesh.cost : 0;
    if (plan.pack && plan.pack.available && plan.pack.variantId) total += plan.packCount * plan.pack.price;
    if (config.clips.available && config.clips.variantId) total += plan.clipBoxes * config.clips.price;
    if (config.barbed.available && config.barbed.variantId) total += (plan.topBarbRolls + plan.lowBarbRolls) * config.barbed.price;
    return total;
  }

  function row(label, value) {
    return '<div class="efx__row"><span>' + label + '</span><b>' + value + '</b></div>';
  }

  function item(title, detail, amount) {
    return '<li><span><strong>' + title + '</strong><small>' + detail + '</small></span><span class="efx__amount">' + amount + '</span></li>';
  }

  function render(container, plan, config) {
    var output = container.querySelector('[data-ef-output]');
    if (!output) return;
    if (plan.error) {
      output.innerHTML = '<div class="efx__error" role="alert">' + escapeHtml(plan.error) + '</div>';
      return;
    }

    var lengthLabel = plan.boundary >= 1000 ? formatNumber(plan.boundary / 1000) + 'km' : formatNumber(plan.boundary) + 'm';
    var html = '<div class="efx__result-head"><h3>Your ' + lengthLabel + ' fence plan</h3></div>';
    html += '<div class="efx__prices">';
    html += '<div class="efx__price"><span>Fixed Knot mesh</span><b>' + (plan.mesh ? formatMoney(plan.meshPerMetre) : 'Quote') + '</b><small>per metre supplied</small></div>';
    html += '<div class="efx__price"><span>240cm Outback Pickets</span><b>' + (plan.pack ? (plan.packPriced ? formatMoney(plan.picketPerUnit) : 'Quote') : 'Not included') + '</b><small>' + (plan.pack ? (plan.packPriced ? 'per picket · 200-pack minimum' : 'price on request · not in total') : 'using your own pickets') + '</small></div>';
    html += '<div class="efx__price efx__price--total"><span>' + (plan.pack && !plan.packPriced ? 'Known subtotal (pickets extra)' : 'Estimated materials total') + '</span><b>' + formatMoney(plan.knownSubtotal) + '</b><small>inc GST · freight separate</small></div>';
    html += '</div><ul class="efx__lines">';

    if (plan.netLength === 0) {
      html += item('Fixed Knot 16/180/15', 'No mesh required', formatMoney(0));
    } else if (!plan.mesh) {
      html += item('Fixed Knot 16/180/15', 'Live product data unavailable—request a quote', 'Quote');
    } else {
      var meshDescription = plan.mesh.lines.map(function (line) { return line.quantity + ' × ' + line.size + 'm roll'; }).join(' + ');
      html += item('Fixed Knot 16/180/15', meshDescription + '<br>' + formatNumber(plan.mesh.supplied) + 'm supplied · ' + formatNumber(plan.mesh.spare) + 'm spare', formatMoney(plan.mesh.cost));
    }

    if (plan.pack && !plan.packPriced) {
      html += item('240cm Outback Pickets', formatNumber(plan.picketsRequired, 0) + ' required at ' + formatNumber(plan.spacing) + 'm spacing<br>Price on request—call Daryl', 'Quote');
    } else if (plan.pack) {
      html += item('240cm Outback Pickets', formatNumber(plan.picketsRequired, 0) + ' required at ' + formatNumber(plan.spacing) + 'm spacing<br>' + plan.packCount + ' × 200-pack · ' + formatNumber(plan.picketsSupplied, 0) + ' supplied · ' + formatNumber(plan.picketsSpare, 0) + ' spare', formatMoney(plan.packCount * plan.pack.price));
    } else {
      html += item('240cm Outback Pickets', formatNumber(plan.picketsRequired, 0) + ' required—using your own pickets', 'Not included');
    }

    html += item('3.25m exclusion strainers', formatNumber(plan.strainerPosts, 0) + ' posts at ' + formatMoney(config.strainer.postPrice) + ' each<br>Post only—stays and assembly hardware to confirm', formatMoney(plan.strainerPosts * config.strainer.postPrice));
    html += item('C24 clips', formatNumber(plan.clipCount, 0) + ' clips required · ' + formatNumber(plan.clipBoxes, 0) + ' × 10,000 box', formatMoney(plan.clipBoxes * config.clips.price));
    var totalBarbRolls = plan.topBarbRolls + plan.lowBarbRolls;
    html += item('HT barbed wire', totalBarbRolls ? formatNumber(totalBarbRolls, 0) + ' × 500m roll' : 'None selected', totalBarbRolls ? formatMoney(totalBarbRolls * config.barbed.price) : '—');
    html += '</ul>';

    if ((plan.topBarbRolls + plan.lowBarbRolls) > 0 && !config.barbed.available) {
      html += '<p class="efx__note">Barbed wire uses the current Shopify price (' + formatMoney(config.barbed.price) + '/500m) but is unavailable for normal cart purchase, so it will not be added.</p>';
    }
    html += '<details class="efx__details"><summary>See calculation details</summary>';
    html += row('Boundary length', formatNumber(plan.boundary) + 'm');
    if (plan.gateWidth > 0) html += row('Gate openings (' + plan.gateways + ')', '−' + formatNumber(plan.gateWidth) + 'm');
    html += row('Net mesh length', formatNumber(plan.netLength) + 'm');
    html += row('Picket positions', formatNumber(plan.picketsRequired, 0));
    html += row('Top barb rolls', formatNumber(plan.topBarbRolls, 0));
    html += row('Low-barb rolls', formatNumber(plan.lowBarbRolls, 0));
    if (plan.groundProtection === 'hinged-apron') html += row('Hinged apron', formatNumber(plan.netLength) + 'm—quoted separately');
    if (plan.groundProtection === 'radius-apron') html += row('Radius apron', 'Information only');
    if (plan.gateWidth > 0) html += row('Exclusion gates', 'Quote only');
    html += '</details>';
    html += '<p class="efx__note"><b>What the total includes:</b> mesh, selected picket packs, C24 boxes, selected barbed wire and the $110 post-only strainer allowance. Freight, stays, assembly hardware, aprons and gates are confirmed separately.' + (plan.pack && !plan.packPriced ? ' 240cm pickets are priced on request and are not in this total.' : '') + '</p>';
    html += '<div class="efx__advisory">Long runs, gullies, watercourses, gateways and changes in direction may require additional strainer assemblies. Outback will confirm the final layout with your plan.</div>';
    html += '<div class="efx__actions">';
    if (cartItems(plan, config).length) html += '<button class="btn btn--gold" type="button" data-ef-add>Add available materials to cart</button>';
    html += '<button class="btn btn--line" type="button" data-ef-quote>Get delivered pricing</button>';
    html += '<button class="btn btn--line" type="button" data-ef-copy>Copy material list</button>';
    html += '<button class="btn btn--line" type="button" data-ef-download>Save material list</button>';
    html += '<a class="btn btn--line" href="tel:+61421572245">Call Daryl</a>';
    html += '</div><p class="efx__note">Available materials cart subtotal: <b>' + formatMoney(cartSubtotal(plan, config)) + ' inc GST.</b> Planning strainers and unavailable or quote-only items are not added. Freight is separate.</p>';
    html += '<p class="efx__note" data-ef-copy-status role="status"></p>';
    output.innerHTML = html;
  }

  function updateVisibility(container) {
    var spacing = container.querySelector('[name="ef_spacing"]:checked');
    var layout = container.querySelector('[name="ef_layout"]:checked');
    var customSpacing = container.querySelector('[data-ef-custom-spacing]');
    var customStrainers = container.querySelector('[data-ef-custom-strainers]');
    var gates = container.querySelector('[data-ef-gates]');
    if (customSpacing) customSpacing.hidden = !spacing || spacing.value !== 'custom';
    if (customStrainers) customStrainers.hidden = !layout || layout.value !== 'custom';
    if (gates) gates.hidden = !layout || layout.value !== 'closed';
    var spacingSummary = container.querySelector('[data-ef-spacing-summary]');
    if (spacingSummary && spacing) {
      var selectedSpacing = spacing.value === 'custom' ? (container.querySelector('[name="ef_custom_spacing"]') || {}).value : spacing.value;
      spacingSummary.textContent = selectedSpacing + 'm selected.';
    }
    var layoutSummary = container.querySelector('[data-ef-layout-summary]');
    if (layoutSummary && layout) {
      if (layout.value === 'closed') layoutSummary.textContent = 'Closed paddock: four corner strainers; gate openings are deducted from mesh.';
      else if (layout.value === 'custom') layoutSummary.textContent = 'Custom layout: enter the number of end and corner strainer posts.';
      else layoutSummary.textContent = 'Straight run: one strainer post at each end.';
    }
  }

  function initialise(container) {
    var configElement = container.querySelector('[data-ef-config]');
    if (!configElement) return;
    var config;
    try { config = JSON.parse(configElement.textContent); } catch (error) { return; }
    var lastPlan;
    var addPending = false;
    var addFeedback = container.querySelector('[data-ef-cart-feedback]');
    var planInput = container.querySelector('[data-ef-plan-input]');
    var lead = container.querySelector('[data-ef-lead]');
    var feedback = lead && lead.querySelector('[data-ef-feedback]');
    var savedPlanKey = 'outback-exclusion-plan-inputs';
    if (!feedback) {
      try {
        var carriedLength = Number(new URLSearchParams(root.location.search).get('ef_length_m'));
        if (Number.isFinite(carriedLength) && carriedLength > 0) {
          var lengthField = container.querySelector('[name="ef_length"]');
          var unitField = container.querySelector('[name="ef_unit"]');
          if (lengthField && unitField) { lengthField.value = String(carriedLength); unitField.value = 'm'; }
        }
      } catch (error) { /* An absent or invalid handoff leaves the normal default plan intact. */ }
    }
    if (feedback) {
      lead.hidden = false;
      try {
        if (feedback.getAttribute('data-ef-feedback') === 'error') {
          var savedInputs = JSON.parse(root.sessionStorage.getItem(savedPlanKey) || 'null');
          if (savedInputs && typeof savedInputs === 'object') {
            var inputNames = { length: 'ef_length', unit: 'ef_unit', spacing: 'ef_spacing', customSpacing: 'ef_custom_spacing', finish: 'ef_finish', topProtection: 'ef_top', groundProtection: 'ef_ground', layout: 'ef_layout', customStrainers: 'ef_custom_strainers', gateways: 'ef_gateways', gateWidth: 'ef_gate_width' };
            Object.keys(inputNames).forEach(function (key) {
              if (savedInputs[key] == null) return;
              if (key === 'finish' && savedInputs[key] === 'own') return;
              container.querySelectorAll('[name="' + inputNames[key] + '"]').forEach(function (field) {
                if (field.type === 'radio') field.checked = field.value === String(savedInputs[key]);
                else field.value = String(savedInputs[key]);
              });
            });
            var ownPickets = container.querySelector('[name="ef_own_pickets"]');
            if (ownPickets) ownPickets.checked = savedInputs.finish === 'own';
          }
        } else root.sessionStorage.removeItem(savedPlanKey);
      } catch (error) { /* Storage can be disabled; visible feedback asks the customer to review the recalculated list. */ }
    }

    function syncPlan() {
      if (planInput) planInput.value = lastPlan && !lastPlan.error ? materialList(lastPlan, config) : '';
      var submit = lead && lead.querySelector('[type="submit"]');
      if (submit) submit.disabled = !lastPlan || !!lastPlan.error;
    }

    function showPlan() {
      syncPlan();
      if (lead) {
        lead.hidden = false;
        var heading = lead.querySelector('[data-ef-lead-heading]');
        if (heading) heading.focus({ preventScroll: true });
        lead.scrollIntoView({ block: 'start' });
      }
    }

    function syncAddState() {
      var button = container.querySelector('[data-ef-add]');
      if (button) {
        button.disabled = addPending;
        if (addPending) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
      }
    }

    function setAddFeedback(message, checkCart) {
      if (!addFeedback) return;
      addFeedback.textContent = message;
      if (checkCart && root.OB_CART_URL && root.OB_CART_CHECK_LABEL) {
        var link = document.createElement('a');
        link.href = root.OB_CART_URL;
        link.textContent = root.OB_CART_CHECK_LABEL;
        addFeedback.appendChild(document.createTextNode(' '));
        addFeedback.appendChild(link);
      }
      addFeedback.hidden = !message;
    }

    function run() {
      updateVisibility(container);
      lastPlan = calculatePlan(readInputs(container), config);
      render(container, lastPlan, config);
      syncAddState();
      syncPlan();
    }

    function onPlanChange(event) {
      if (event.target.name && event.target.name.indexOf('ef_') === 0) run();
    }
    container.addEventListener('input', onPlanChange);
    container.addEventListener('change', onPlanChange);
    if (lead) lead.addEventListener('submit', function (event) {
      run();
      if (!lastPlan || lastPlan.error) { event.preventDefault(); container.querySelector('[data-ef-output]').scrollIntoView({ block: 'center' }); }
      else {
        try { root.sessionStorage.setItem(savedPlanKey, JSON.stringify(readInputs(container))); } catch (error) { /* Do not prevent the contact form when storage is unavailable. */ }
      }
    });
    container.addEventListener('click', function (event) {
      var addButton = event.target.closest('[data-ef-add]');
      var quoteButton = event.target.closest('[data-ef-quote]');
      var copyButton = event.target.closest('[data-ef-copy]');
      var downloadButton = event.target.closest('[data-ef-download]');
      if (addButton && !addPending && lastPlan && !lastPlan.error) {
        var items = cartItems(lastPlan, config);
        if (items.length) {
          addPending = true;
          syncAddState();
          setAddFeedback(config.addPendingMessage || '', false);
          var addFailed = false;
          document.dispatchEvent(new CustomEvent('ob:addItems', { detail: {
            items: items,
            onError: function (error) {
              addFailed = true;
              var blocked = error && error.cartAddBlocked;
              setAddFeedback(blocked ? error.message : config.addErrorMessage || '', !blocked);
            },
            onComplete: function () {
              addPending = false;
              syncAddState();
              if (!addFailed) setAddFeedback(config.addSuccessMessage || '', true);
            }
          } }));
        }
      }
      if (quoteButton && lastPlan && !lastPlan.error) showPlan();
      if (downloadButton && lastPlan && !lastPlan.error) {
        var url = root.URL.createObjectURL(new Blob([materialList(lastPlan, config)], { type: 'text/plain;charset=utf-8' }));
        var link = document.createElement('a');
        link.href = url;
        link.download = 'outback-exclusion-fence-materials.txt';
        document.body.appendChild(link);
        link.click();
        link.remove();
        root.setTimeout(function () { root.URL.revokeObjectURL(url); }, 1000);
      }
      if (copyButton && lastPlan && !lastPlan.error) {
        function copyFallback() {
          showPlan();
          if (planInput) { planInput.focus(); planInput.select(); }
          var status = container.querySelector('[data-ef-copy-status]');
          if (status) status.textContent = 'Automatic copy is unavailable. Your material list is selected below — copy it manually or use Save material list.';
        }
        if (!navigator.clipboard || !navigator.clipboard.writeText) { copyFallback(); return; }
        navigator.clipboard.writeText(materialList(lastPlan, config)).then(function () {
          copyButton.textContent = 'Copied ✓';
          root.setTimeout(function () { copyButton.textContent = 'Copy material list'; }, 1800);
        }).catch(copyFallback);
      }
    });
    document.addEventListener('click', function (event) {
      var link = event.target.closest('[data-ef-open-quote]');
      if (link && link.getAttribute('href') === '#' + container.id && lastPlan && !lastPlan.error) {
        event.preventDefault();
        showPlan();
      }
    });
    run();
    if (feedback) {
      feedback.focus({ preventScroll: true });
      feedback.scrollIntoView({ block: 'start' });
    }
  }

  var api = { optimiseRolls: optimiseRolls, calculatePlan: calculatePlan, cartItems: cartItems, cartSubtotal: cartSubtotal, materialList: materialList };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.OutbackExclusionPlanner = api;

  if (typeof document !== 'undefined') {
    function start() { document.querySelectorAll('[data-exclusion-planner]').forEach(initialise); }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
  }
}(typeof window !== 'undefined' ? window : (typeof global !== 'undefined' ? global : this)));
